/**
 * Hydrate Products/Articles tab counts for the inactive tab.
 * Figma 14216:127851 shows both counts even when a tab is inactive.
 * Active count comes from Liquid; inactive count is fetched via ?view=count
 * (query + type only — product filters must not affect the articles count).
 */

const COUNT_STORAGE_KEY = 'search-resource-tab-counts';

/** @type {Map<string, Promise<number | null>>} */
const countRequests = new Map();

/** @type {Map<string, number>} */
const resolvedCounts = new Map();

/**
 * @param {string} template
 * @param {number} count
 * @returns {string}
 */
function formatTabLabel(template, count) {
  return template.replaceAll('__COUNT__', String(count));
}

/**
 * @param {string} terms
 * @param {'product' | 'article'} type
 * @returns {string}
 */
function getCountCacheKey(terms, type) {
  return `${type}:${terms}`;
}

/**
 * @param {string} terms
 * @param {'product' | 'article'} type
 * @returns {number | null}
 */
function getStoredCount(terms, type) {
  const cacheKey = getCountCacheKey(terms, type);
  const resolved = resolvedCounts.get(cacheKey);
  if (resolved != null) return resolved;

  try {
    const stored = JSON.parse(sessionStorage.getItem(COUNT_STORAGE_KEY) || '{}');
    const count = Number(stored[cacheKey]);
    return Number.isFinite(count) ? count : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} terms
 * @param {'product' | 'article'} type
 * @param {number} count
 */
function storeCount(terms, type, count) {
  const cacheKey = getCountCacheKey(terms, type);
  resolvedCounts.set(cacheKey, count);

  try {
    const stored = JSON.parse(sessionStorage.getItem(COUNT_STORAGE_KEY) || '{}');
    stored[cacheKey] = count;
    sessionStorage.setItem(COUNT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * @param {HTMLElement} tab
 * @returns {boolean}
 */
function tabNeedsCount(tab) {
  if (tab.getAttribute('aria-current') === 'page') return false;

  const text = tab.textContent || '';
  if (/[…]/.test(text)) return true;

  return !tab.dataset.searchTabCount;
}

/**
 * @param {string} terms
 * @param {'product' | 'article'} type
 * @returns {Promise<number | null>}
 */
function fetchSearchCount(terms, type) {
  const cacheKey = getCountCacheKey(terms, type);
  const stored = getStoredCount(terms, type);
  if (stored != null) {
    return Promise.resolve(stored);
  }

  const cached = countRequests.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}search`, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('type', type);
    url.searchParams.set('view', 'count');

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;

      const text = (await response.text()).trim();
      const data = JSON.parse(text);
      const count = Number(data?.count);

      if (Number.isFinite(count)) {
        storeCount(terms, type, count);
        return count;
      }

      return null;
    } catch {
      return null;
    }
  })();

  countRequests.set(cacheKey, request);
  return request;
}

/**
 * Apply a count to every matching tab on the page (desktop + mobile).
 * @param {'product' | 'article'} type
 * @param {number} count
 * @param {string} [terms]
 */
function applyCountToTabs(type, count, terms) {
  const tabs = document.querySelectorAll(
    `.search-resource-tabs [data-search-tab-type="${type}"]`
  );

  for (const tab of tabs) {
    if (!(tab instanceof HTMLElement)) continue;
    if (tab.getAttribute('aria-current') === 'page') continue;

    const template = tab.dataset.countTemplate;
    if (!template) continue;

    tab.textContent = formatTabLabel(template, count);
    tab.dataset.searchTabCount = String(count);
  }

  if (terms) {
    storeCount(terms, type, count);
  }
}

/**
 * Restore cached inactive counts immediately (e.g. after facet section morph).
 * @param {HTMLElement} nav
 */
function applyCachedCounts(nav) {
  const terms = nav.dataset.searchTerms || '';
  if (!terms) return;

  for (const type of ['product', 'article']) {
    const count = getStoredCount(terms, /** @type {'product' | 'article'} */ (type));
    if (count == null) continue;
    applyCountToTabs(/** @type {'product' | 'article'} */ (type), count, terms);
  }
}

/**
 * @param {HTMLElement} nav
 */
async function hydrateSearchResourceTabs(nav) {
  const terms = nav.dataset.searchTerms || '';
  if (!terms) return;

  applyCachedCounts(nav);

  const tabs = nav.querySelectorAll('[data-search-tab-type]');
  const tabsToHydrate = [...tabs].filter(
    (tab) => tab instanceof HTMLElement && tabNeedsCount(/** @type {HTMLElement} */ (tab))
  );

  if (tabsToHydrate.length === 0) return;

  for (const tab of tabsToHydrate) {
    if (!(tab instanceof HTMLElement)) continue;

    const type = tab.dataset.searchTabType;
    if (type !== 'product' && type !== 'article') continue;

    const count = await fetchSearchCount(terms, type);
    if (count == null) continue;

    applyCountToTabs(type, count, terms);
  }
}

function initSearchResourceTabs() {
  const navs = document.querySelectorAll('.search-resource-tabs[data-hydrate-counts]');

  for (const nav of navs) {
    if (nav instanceof HTMLElement) {
      watchSearchResourceTabs(nav);
      hydrateSearchResourceTabs(nav);
    }
  }
}

/**
 * Re-apply cached counts when facet section morph resets inactive tabs to "…".
 * @param {HTMLElement} nav
 */
function watchSearchResourceTabs(nav) {
  if (nav.dataset.tabsObserver === 'true') return;
  nav.dataset.tabsObserver = 'true';

  const observer = new MutationObserver(() => {
    const needsRestore = [...nav.querySelectorAll('[data-search-tab-type]')].some(
      (tab) => tab instanceof HTMLElement && tabNeedsCount(tab)
    );

    if (!needsRestore) return;

    applyCachedCounts(nav);
    hydrateSearchResourceTabs(nav);
  });

  observer.observe(nav, { childList: true, subtree: true, characterData: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchResourceTabs, { once: true });
} else {
  initSearchResourceTabs();
}

document.addEventListener('shopify:section:load', initSearchResourceTabs);

/** Re-hydrate tab counts after product filters re-render the search section. */
document.addEventListener('shopify:search:update', (event) => {
  const promise = /** @type {{ promise?: Promise<unknown> } | undefined} */ (
    event.detail
  )?.promise;

  if (promise) {
    promise.finally(initSearchResourceTabs);
    return;
  }

  initSearchResourceTabs();
});
